import { get, post, put, del } from '../utils/request';

// Lead status enum
export enum LeadStatus {
  PUBLIC_POOL = 'PUBLIC_POOL',
  ASSIGNED = 'ASSIGNED',
  CONVERTED = 'CONVERTED',
  RECYCLED = 'RECYCLED',
}

// Attachment interface
export interface Attachment {
  id: number;
  file_name?: string;  // 旧字段名
  filename?: string;   // 新字段名（后端返回）
  file_path: string;
  file_size?: number;
  file_type?: string;
  entity_type?: string;
  entity_id?: number;
  uploaded_by?: number;
  uploader_name?: string;
  created_at?: string;
  updated_at?: string;
}

// 线索手机号码（一个线索可有多个号码，其中一个为主号码）
export interface LeadPhoneItem {
  id?: number;
  phone: string;
  label?: string;
  is_primary?: boolean;
}

// Lead interface
export interface Lead {
  id: number;
  lead_number?: string;
  contact_name: string;
  phone?: string;
  phones?: LeadPhoneItem[];
  email?: string;
  address?: string;
  province?: string;
  city?: string;
  district?: string;
  data_source?: string;
  lead_type?: string;
  case_description?: string;
  description?: string;
  follow_status?: string; // 跟进意向（主要字段）
  last_follow_time?: string; // 最后跟进时间
  next_follow_time?: string;
  next_follow_up_time?: string; // Alternative field name
  reminder_enabled?: number;
  urgency_level?: string;
  intention_level?: string; // 旧字段名（兼容）
  visit_time?: string;
  lead_generated_time?: string;
  status: LeadStatus;
  assigned_to?: number;
  assigned_at?: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
  creator_name?: string;
  assignee_name?: string;
  collaborators?: string[]; // 协作者名字列表
  is_collaborator?: boolean; // 当前用户是否是协作者
  attachments?: Attachment[];
  tags?: string | string[]; // New field for tags
  is_blacklisted?: boolean; // 是否在黑名单中
  blacklist_remark?: string; // 黑名单备注
  is_new_unfollowed?: boolean; // 分配后一直未跟进的新线索（我的线索置顶标识）
}

// Phone check response interface
export interface PhoneCheckResponse {
  phone: string;
  is_blacklisted: boolean;
  blacklist_remark?: string;
  has_existing_leads: boolean;
  existing_leads: Array<{
    id: number;
    lead_number: string;
    contact_name: string;
    status: string;
    follow_status?: string;
    assigned_to?: number;
    assignee_name?: string;
    created_at: string;
  }>;
}

// Lead list request interface
export interface LeadListRequest {
  page?: number;
  pageSize?: number;
  page_size?: number;
  status?: LeadStatus;
  search?: string;
  keyword?: string;
  data_source?: string[];
  lead_type?: string[];
  assigned_to?: number | number[];
  follow_status?: string[];
  province?: string;
  city?: string;
  district?: string;
  urgency_level?: string[];
}

// Lead list response interface
export interface LeadListResponse {
  items: Lead[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// Create lead request interface
export interface CreateLeadRequest {
  contact_name: string;
  phone?: string;
  phones?: LeadPhoneItem[];
  email?: string;
  address?: string;
  province?: string;
  city?: string;
  district?: string;
  data_source?: string;
  lead_type?: string;
  case_description?: string;
  description?: string;
  follow_status?: string;
  next_follow_time?: string;
  reminder_enabled?: number;
  urgency_level?: string;
  visit_time?: string;
  lead_generated_time?: string;
  assigned_to?: number;
}

// 取线索的可用号码列表（优先子表，缺省回退主号快照）
export function getLeadPhones(lead: Pick<Lead, 'phone' | 'phones'>): LeadPhoneItem[] {
  if (lead.phones && lead.phones.length > 0) return lead.phones;
  return lead.phone ? [{ phone: lead.phone, is_primary: true }] : [];
}

// Update lead request interface
export interface UpdateLeadRequest extends Partial<CreateLeadRequest> {
  status?: LeadStatus;
  tags?: string | string[]; // Support tags update
  follow_status?: string; // Support follow status update
  intention_level?: string; // Support intention level update (legacy)
  next_follow_up_time?: string; // Support next follow-up time update
}

// Lead statistics interface
export interface LeadStatistics {
  total_leads: number;
  public_pool_leads: number;
  assigned_leads: number;
  converted_leads: number;
  recycled_leads: number;
  my_leads: number;
  conversion_rate: number;
  my_conversion_rate?: number;
  today_followed?: number;
  today_new_leads?: number;
}

/**
 * Get all leads (for selection purposes)
 */
export const getAllLeads = async (params: LeadListRequest): Promise<LeadListResponse> => {
  return get<LeadListResponse>('/leads', { params });
};

/**
 * Get public pool leads
 */
export const getPublicPoolLeads = async (params: LeadListRequest): Promise<LeadListResponse> => {
  return get<LeadListResponse>('/leads/public-pool', { params });
};

/**
 * Get my leads
 */
export const getMyLeads = async (params: LeadListRequest): Promise<LeadListResponse> => {
  return get<LeadListResponse>('/leads/my-leads', { params });
};

/**
 * Get lead by ID
 */
export const getLeadById = async (id: number): Promise<Lead> => {
  return get<Lead>(`/leads/${id}`);
};

/**
 * Get lead by ID (alias for consistency)
 */
export const getLead = async (id: number): Promise<Lead> => {
  return getLeadById(id);
};

/**
 * Create new lead
 */
export const createLead = async (data: CreateLeadRequest): Promise<Lead> => {
  return post<Lead>('/leads', data);
};

/**
 * Update lead
 */
export const updateLead = async (
  id: number, 
  data: UpdateLeadRequest, 
  skipSystemRecord: boolean = false
): Promise<Lead> => {
  const url = skipSystemRecord ? `/leads/${id}?skip_system_record=true` : `/leads/${id}`;
  return put<Lead>(url, data);
};

/**
 * Delete lead
 */
export const deleteLead = async (id: number): Promise<void> => {
  return del<void>(`/leads/${id}`);
};

/**
 * Get lead statistics
 */
export const getLeadStatistics = async (): Promise<LeadStatistics> => {
  return get<LeadStatistics>('/leads/statistics');
};

/**
 * Check phone for existing leads and blacklist
 */
export const checkPhone = async (phone: string): Promise<PhoneCheckResponse> => {
  return get<PhoneCheckResponse>(`/leads/check-phone/${phone}`);
};

/**
 * Filter options interface
 */
export interface FilterOptions {
  data_sources: string[];
  provinces: string[];
  cities: string[];
  districts: string[];
  urgency_levels: string[];
  follow_statuses: string[];
  lead_types: string[];
}

/**
 * Get filter options for lead search
 */
export const getFilterOptions = async (): Promise<FilterOptions> => {
  return get<FilterOptions>('/leads/filter-options');
};

/**
 * Claim lead from public pool
 */
export const claimLead = async (id: number): Promise<void> => {
  return post<void>(`/leads/${id}/claim`);
};

/**
 * Release lead back to public pool
 */
export const releaseLead = async (id: number): Promise<void> => {
  return post<void>(`/leads/${id}/release`);
};

/**
 * Batch claim leads from public pool
 */
export const batchClaimLeads = async (leadIds: number[]): Promise<{
  msg: string;
  success_count: number;
  failed_count: number;
  failed_leads: Array<{ id: number; reason: string }>;
}> => {
  return post('/leads/batch-claim', leadIds);
};

/**
 * Batch release leads back to public pool
 */
export const batchReleaseLeads = async (leadIds: number[]): Promise<{
  msg: string;
  success_count: number;
  failed_count: number;
  failed_leads: Array<{ id: number; reason: string }>;
}> => {
  return post('/leads/batch-release', leadIds);
};

/**
 * Batch assign leads to user (admin only)
 */
export const batchAssignLeads = async (leadIds: number[], assignedTo: number): Promise<{
  msg: string;
  count: number;
}> => {
  return post('/leads/batch-assign', {
    lead_ids: leadIds,
    assigned_to: assignedTo,
  });
};

/**
 * Batch delete leads (soft delete)
 */
export const batchDeleteLeads = async (leadIds: number[]): Promise<{
  msg: string;
  success_count: number;
  failed_count: number;
  failed_leads: Array<{ id: number; reason: string }>;
}> => {
  return post('/leads/batch-delete', leadIds);
};


// ==================== 按比例分配相关接口 ====================

/**
 * 分配比例项
 */
export interface AssignmentRatio {
  user_id: number;
  ratio: number;  // 百分比（如30表示30%）
}

/**
 * 按比例分配请求
 */
export interface ProportionalAssignRequest {
  lead_ids: number[];
  assignments: AssignmentRatio[];
}

/**
 * 平均分配请求
 */
export interface AverageAssignRequest {
  lead_ids: number[];
  user_ids: number[];
}

/**
 * 分配结果详情
 */
export interface AssignResultDetail {
  user_id: number;
  count: number;
  ratio?: number;
  lead_ids?: number[];
}

/**
 * 分配结果响应
 */
export interface AssignResultResponse {
  msg: string;
  total: number;
  details: AssignResultDetail[];
}

/**
 * 智能分配建议 - 单个用户
 */
export interface SmartSuggestionUser {
  user_id: number;
  real_name: string;
  current_leads: number;
  max_leads: number;
  available_capacity: number;
  utilization: number;  // 当前利用率百分比
  suggested_ratio_capacity: number;  // 容量策略建议比例
  suggested_count_capacity: number;  // 容量策略建议数量
  suggested_ratio_balance: number;  // 均衡策略建议比例
  suggested_count_balance: number;  // 均衡策略建议数量
  recommended_ratio: number;  // 推荐比例
  recommended_count: number;  // 推荐数量
  strategy: string;  // 推荐策略类型
}

/**
 * 智能分配建议响应
 */
export interface SmartSuggestionResponse {
  lead_count: number;
  suggestions: SmartSuggestionUser[];
  strategy: 'balance' | 'capacity';
}

/**
 * 按比例批量分配线索
 */
export const proportionalAssignLeads = async (
  data: ProportionalAssignRequest
): Promise<AssignResultResponse> => {
  return post('/leads/proportional-assign', data);
};

/**
 * 平均分配线索
 */
export const averageAssignLeads = async (
  data: AverageAssignRequest
): Promise<AssignResultResponse> => {
  return post('/leads/average-assign', data);
};

/**
 * 获取智能分配建议
 */
export const getSmartAssignSuggestion = async (
  leadCount: number,
  userIds: number[]
): Promise<SmartSuggestionResponse> => {
  return get('/leads/smart-assign-suggestion', {
    params: {
      lead_count: leadCount,
      user_ids: userIds.join(',')
    }
  });
};